import type { ClientTraceStep } from '@travel-planner/shared/utils';
import { invitationTraceId } from '@travel-planner/shared/utils';
import { reportClientEvent } from '@/shared/lib/report-client-event';
import { readTicketClaims } from './invitation-ticket';

/**
 * Report one browser step of the invitation → registration trace (XT-706).
 *
 * The server can only see what reaches it, so the steps that mattered most in
 * the bug this was built for — the link being opened, Clerk answering something
 * other than success, a form that silently went nowhere — are invisible without
 * this. Rides the existing `/client-events` beacon, which is public and
 * auth-free precisely so it keeps working while sign-in is broken.
 *
 * Never throws: `reportClientEvent` swallows everything, and a diagnostic must
 * never be the reason a registration fails.
 */
export const reportInviteTrace = (
  step: ClientTraceStep,
  input: {
    /** When known. The trace id is derived from it. */
    email?: string | null;
    /** Carries the invitation's ids when the email is not known yet. */
    ticket?: string;
    fields?: Record<string, string | number | boolean | null | undefined>;
  } = {}
): void => {
  const claims = readTicketClaims(input.ticket);
  const meta: Record<string, string | number | boolean | null> = {
    step,
    // Sent so the server can group without re-deriving; it recomputes from the
    // email anyway (this endpoint is public, so nothing here is trusted).
    traceId: invitationTraceId(input.email),
  };
  if (input.email) meta.email = input.email;
  // `sid` is the INVITATION id — the only thing that ties this visit to the
  // invite that produced it, since the ticket carries no email address.
  // `iid` is the Clerk INSTANCE id: identical for every user on the deployment,
  // so it identifies nothing and is deliberately not reported.
  if (claims.sid) meta.sid = claims.sid;
  for (const [key, value] of Object.entries(input.fields ?? {})) {
    if (value === undefined) continue;
    meta[key] = value;
  }

  reportClientEvent({ kind: 'invite_trace', meta });
};

/** Clerk's error code and message, shaped for one trace line. */
export const clerkErrorFields = (
  err: unknown
): { code?: string; msg?: string } => {
  if (typeof err === 'object' && err !== null && 'errors' in err) {
    const { errors } = err as { errors?: unknown };
    const first = Array.isArray(errors)
      ? (errors.at(0) as { code?: unknown; message?: unknown } | undefined)
      : undefined;
    if (first) {
      return {
        code: typeof first.code === 'string' ? first.code : undefined,
        msg:
          typeof first.message === 'string'
            ? first.message.slice(0, 200)
            : undefined,
      };
    }
  }
  if (err instanceof Error) return { msg: err.message.slice(0, 200) };
  return {};
};
